/* eslint-disable react/prop-types */
/** @jsxImportSource @emotion/react */
import React from 'react';
import { useSelector } from 'react-redux';
import { Skeleton, useTokens } from '@nike/nike-design-system-components';

import OrdersTableHeaderRow from './OrdersTableHeaderRow';
import OrdersTableRow from './OrdersTableRow';
import LoadingAnimation from '../../components/LoadingAnimation';

import { orderDetailsReportSelectors } from './reducer';
import { selectDepartments, getUserAccount } from '../../user/selectors';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

const OrdersTable = ({ rootRef, targetRef }) => {
  const tokens = useTokens();

  const orders = useSelector(orderDetailsReportSelectors.orders);
  const totalRecords = useSelector(orderDetailsReportSelectors.totalRecords);
  const rowsLoading = useSelector(orderDetailsReportSelectors.requestOrdersInProgress);
  const userDepts = useSelector(selectDepartments);
  const userAccount = useSelector(getUserAccount)?.number;
  const deptNameMapping =
    userDepts
      .find(dept => dept.customerNumber === userAccount)
      ?.customerDepartments?.reduce(
        (deptsByNumber, dept) => ({
          ...deptsByNumber,
          [dept.customerDepartmentCode]: dept.customerDepartmentName
        }),
        {}
      ) || {};
  const noOrders = orders.length === 0;

  const boxShadow =
    `inset 0px 1px 0px 0px ${tokens.podiumCdsColorGrey200}` +
    `, inset -1px 0px 0px 0px ${tokens.podiumCdsColorGrey200}` +
    `, inset 1px 0px 0px 0px ${tokens.podiumCdsColorGrey200}` +
    `, inset 0px -1px 0px 0px ${tokens.podiumCdsColorGrey200}`;

  return (
    <>
      <div css={{ marginTop: '30px', fontSize: '14px' }}>
        {totalRecords} <TranslatedCopy id='global.results' />
      </div>
      <div css={{ height: '400px', overflowY: 'scroll', marginBottom: '8px' }}>
        <table
          ref={rootRef}
          css={{
            display: 'block',
            height: '100%',
            overflowX: 'hidden',
            overflowY: 'scroll',
            textAlign: 'left',
            fontSize: '14px',
            td: { padding: '10px', overflowWrap: 'break-word' },
            th: { padding: '10px' },
            marginTop: '5px'
          }}
        >
          <thead
            css={{
              display: 'table',
              tableLayout: 'fixed',
              width: '100%',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow,
              backgroundColor: tokens.podiumCdsColorGrey50
            }}
          >
            <OrdersTableHeaderRow />
          </thead>
          <tbody
            css={{
              display: 'table',
              height: '150px',
              position: 'relative',
              tableLayout: 'fixed',
              width: '100%',
              color: tokens.podiumCdsColorGrey500
            }}
          >
            {orders.map((order, index) => (
              <OrdersTableRow
                key={order.id}
                order={{ ...order, departmentName: deptNameMapping[order.departmentNumber] }}
                {...(index === orders.length - 3 && { targetRef })}
                boxShadow={boxShadow}
              />
            ))}
            {targetRef?.current && orders.length < totalRecords && (
              <Skeleton
                css={{
                  visibility: !noOrders && rowsLoading ? 'visible' : 'hidden'
                }}
                height={`${targetRef?.current?.clientHeight}px`}
                width={`${targetRef?.current?.clientWidth}px`}
              />
            )}
            {noOrders && rowsLoading && (
              <div
                data-testid='spinner'
                css={{ left: '50%', top: '50%', position: 'absolute', transform: 'translate(-50%, -50%)' }}
              >
                <LoadingAnimation size='s' />
              </div>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OrdersTable;
